<template>
    <v-card width="900" height="750" elevation="8" class="d-flex flex-column">
        <!-- Header -->
        <v-card-title class="primary white--text pa-4">
            <div class="d-flex align-center w-100">
                <v-icon class="mr-2" color="white">mdi-message-text</v-icon>
                <span class="text-h6 font-weight-medium" v-if="custMdn">
                    {{ custMdn | phoneNum }}
                    <template v-if="custName">
                        &nbsp;{{ custName }}
                    </template>
                </span>
            </div>
            <v-btn icon @click="closePopup" color="white" class="ml-auto">
                <v-icon>mdi-close</v-icon>
            </v-btn>
        </v-card-title>

        <!-- Search Section -->
        <v-card-text class="pa-4 pb-2">
            <div class="d-flex align-center">
                <v-text-field
                    v-model="memo_text"
                    placeholder="메모 내용 검색"
                    prepend-inner-icon="mdi-magnify"
                    append-icon="mdi-close"
                    @click:append="memo_text = ''"
                    outlined
                    dense
                    hide-details
                    class="flex-grow-1 mr-3"
                    style="flex: 0 0 66.67%;"
                ></v-text-field>
                <v-select
                    v-model="sortOrder"
                    :items="sortOptions"
                    item-text="text"
                    item-value="value"
                    outlined
                    dense
                    hide-details
                    style="flex: 0 0 150px;"
                ></v-select>
            </div>
        </v-card-text>

        <!-- Info Section -->
        <v-card-text class="pa-4 pt-1" v-if="groupDetail && groupDetail.cust_mdn">
            <v-chip small color="grey lighten-4">
                <v-icon small class="mr-1" :color="getLabelColor(groupDetail.cust_label_type)">mdi-account-circle</v-icon>
                총 {{ memoList.length | number }}개 메모
            </v-chip>
        </v-card-text>

        <!-- Memo List -->
        <v-card-text ref="memoList" class="pa-3 pt-0 flex-grow-1 d-flex flex-column" style="overflow-y: auto;">
            <div v-if="!memoList || memoList.length === 0" class="text-center d-flex flex-column justify-center flex-grow-1">
                <div>
                    <v-icon size="24" color="grey lighten-2">mdi-message-outline</v-icon>
                    <p class="grey--text mt-1 mb-0">등록된 메모가 없습니다</p>
                </div>
            </div>

            <div v-for="(item, idx) in sortedMemoList" :key="'memo_' + idx" class="mb-2">
                <v-card outlined class="rounded-lg">
                    <v-card-text class="pa-2">
                        <div class="d-flex justify-space-between align-center mb-1">
                            <span class="font-weight-medium caption">{{ item.writer_name }}</span>
                            <span class="caption grey--text">{{ item.reg_dt | dateMin }}</span>
                        </div>
                        <div class="grey lighten-5 pa-2 rounded" style="border-left: 2px solid #1976d2; font-size: 13px; line-height: 1.4; word-wrap: break-word; white-space: pre-wrap;">{{ item.memo_text }}</div>
                    </v-card-text>
                </v-card>
            </div>
        </v-card-text>

        <!-- Input Section -->
        <v-card-text class="pa-3 pt-1">
            <v-textarea
                v-model="memo"
                placeholder="메모를 입력하세요."
                outlined
                dense
                hide-details
                rows="2"
            ></v-textarea>
        </v-card-text>

        <!-- Actions -->
        <v-card-actions class="pa-4 pt-2">
            <v-spacer></v-spacer>
            <v-btn
                @click="closePopup"
                text
                class="mr-3"
                width="100"
                height="36"
            >
                닫기
            </v-btn>
            <v-btn
                @click="createMemo"
                color="primary"
                :disabled="!memo.trim()"
                width="120"
                height="36"
                type="button"
            >
                메모 등록
            </v-btn>
        </v-card-actions>
    </v-card>
</template>

<script>
import {mapState} from 'vuex'
import svcHistApi from '@/api/svc_hist'

export default {
    components: {
        'TilePaging': () => import('@/components/common/tile_paging.vue')
    },
    props: ['propCustSeqno', 'propCustMdn', 'propCustName'],
    data: () => ({
        memo: '',
        custSeqno: 0,
        custMdn: '',
        custName: '',
        memo_text: '',
        isInitialLoad: true,
        isCreating: false,
        searchParam: {},
        sortOrder: 'desc',
        sortOptions: [
            { text: '정렬: 최신순', value: 'desc' },
            { text: '정렬: 오래된순', value: 'asc' }
        ]
    }),
    computed: {
        ...mapState('svc_hist_cust_memo', {
            curSearchParam: state => state.memoSearchParam,
            allMemoList: state => state.memoList,
            groupDetail: state => state.groupDetail,
            totalCount: state => state.memoTotalCount,
        }),
        memoList() {
            if (!this.memo_text.trim()) {
                return this.allMemoList
            }
            return this.allMemoList.filter(item => 
                item.memo_text.toLowerCase().includes(this.memo_text.toLowerCase())
            )
        },
        sortedMemoList() {
            if (!this.memoList || this.memoList.length === 0) return []
            this.$refs.memoList.scrollTop = 0
            const list = [...this.memoList]
            return list.sort((a, b) => {
                return this.sortOrder === 'desc' ? b.cust_memo_seqno - a.cust_memo_seqno :a.cust_memo_seqno - b.cust_memo_seqno
            })
        },
        totalPageCount() {
            if (this.totalCount === 0) {
                return 0
            }
            let pageSize = this.searchParam.page_size
            return Math.floor(this.totalCount / pageSize) + (((this.totalCount % pageSize) > 0) ? 1 : 0)
        }
    },
    created() {
        this.custSeqno = this.propCustSeqno
        this.custMdn = this.propCustMdn
        this.custName = this.propCustName
        this.getCustMemoList()
    },
    mounted() {
    },
    methods: {
        getCustMemoList() {
            if (this.custSeqno && this.custSeqno !== 0) {
                this.$store.dispatch('svc_hist_cust_memo/getMemoList', {
                    searchParam: {
                        cust_seqno: parseInt(this.custSeqno)
                    }, scb: () => {
                        if (!this.isInitialLoad) {
                            this.$nextTick(() => {
                                if (this.$refs.memoList) {
                                    if (this.sortOrder === 'desc') {
                                        this.$refs.memoList.scrollTop = 0
                                    } else {
                                        this.$refs.memoList.scrollTop = this.$refs.memoList.scrollHeight
                                    }
                                }
                            })
                        }
                        this.isInitialLoad = false
                    }, fcb: (err) => {
                        alert(err)
                    },
                    va: this
                })
            }
        },
        closePopup() {
            this.$emit('cancel')
        },
        createMemo() {
            if (this.isCreating) return
            if (this.$options.filters.isNull(this.memo)) {
                this.$store.dispatch('openAlert', {message: '메모 내용을 입력해 주세요',})
            } else {
                this.isCreating = true
                svcHistApi.createCustMemo({
                    'cust_mdn': this.groupDetail && this.groupDetail.cust_mdn ? this.groupDetail.cust_mdn : this.custMdn,
                    'memo': this.memo
                }, (custSeqno, va) => {
                    if (this.custSeqno === 0) {
                        this.custSeqno = custSeqno
                    }
                    va.getCustMemoList()
                    va.memo = ''
                    va.isCreating = false
                }, () => {
                    this.$store.dispatch('openAlert', {message: '메모 입력 중 오류가 발생하였습니다',})
                    this.isCreating = false
                }, this)
            }
        },
        getLabelColor(labelType) {
            const colors = {
                '0': 'grey',
                '1': 'blue',
                '2': 'orange',
                '3': 'red',
                '4': 'black'
            }
            return colors[labelType] || 'grey'
        }
    },
    beforeDestroy() {
        this.$store.commit('svc_hist_cust_memo/setMemoList', [])
        this.$store.commit('svc_hist_cust_memo/setGroupDetail', {})
    }
}
</script>